import { useEffect, useRef, useState } from 'react'

import { KeyboardKey, KeyboardShortcut, KeyboardShortcutPreview, VALID_KEYS } from '~/astra'
import { useClickOutside } from '~/hooks/useClickOutside'

type Props = {
    shortcut: KeyboardShortcut
    onShortcutChange: (shortcut: KeyboardShortcut) => void
    error?: boolean
}

export const KeyboardShortcutRecorder = ({ shortcut, onShortcutChange, error }: Props) => {
    const [isRecording, setIsRecording] = useState(false)
    const recorderRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const recorderEl = recorderRef.current
        if (!isRecording || !recorderEl) return

        const handleKeyDown = (e: KeyboardEvent) => {
            e.preventDefault()
            e.stopPropagation()

            if (e.key === 'Escape') {
                setIsRecording(false)
                return
            }

            const key = e.key.toUpperCase()

            if (!VALID_KEYS.has(key)) return

            const newShortcut: KeyboardShortcut = {
                keyboardKey: key as KeyboardKey,
                hasShift: e.shiftKey,
                hasMeta: e.metaKey || e.ctrlKey,
            }

            onShortcutChange(newShortcut)
            setIsRecording(false)
        }

        recorderEl.addEventListener('keydown', handleKeyDown)
        return () => recorderEl.removeEventListener('keydown', handleKeyDown)
    }, [isRecording, onShortcutChange])

    useClickOutside(recorderRef, () => {
        if (isRecording) setIsRecording(false)
    })

    return (
        <button
            ref={recorderRef}
            type="button"
            onClick={() => setIsRecording(prev => !prev)}
            className={`inline-flex h-7 items-center gap-2 rounded-md border px-1.5 py-1 text-body-small transition-all ${
                isRecording ? 'bg-surface-base shadow-sm' : error ? 'border-critical-base' : 'border-base hover:border-hover'
            }`}
        >
            {isRecording ? (
                <span className="animate-pulse text-body-small text-subtle">Trykk en tast...</span>
            ) : (
                <KeyboardShortcutPreview shortcut={shortcut} />
            )}
        </button>
    )
}
